import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
    Container,
    Typography,
    Paper,
    CircularProgress,
    Alert,
    Box,
    Grid,
    Card,
    CardContent,
    Divider,
    Button,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Tabs,
    Tab,
    TableHead,
    TableContainer,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import SendIcon from '@mui/icons-material/Send'
import { apiClient } from '../services/api'

interface ApplicationDetail {
    id: string
    description: string
    statusId: number
    statusName: string
    createdDate: string
    submittedDate: string | null
    approvedDate: string | null
    rejectedDate: string | null
    cancelledDate: string | null
    closedDate: string | null
    sourceApplicationId: string | null
    broker: any | null
    business: any
    borrowers: any[]
    bankInfos: any[]
    financingTermsRequests: any[]
}

const statusOptions = [
    { id: 1, name: 'Submitted', label: 'Submitted' },
    { id: 2, name: 'InProcess', label: 'In Process' },
    { id: 3, name: 'Underwriting', label: 'Underwriting' },
    { id: 4, name: 'Approved', label: 'Approved' },
    { id: 5, name: 'ConditionallyApproved', label: 'Conditionally Approved' },
    { id: 6, name: 'Declined', label: 'Declined' },
]

const getStatusColor = (statusId: number): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    if (statusId === 4 || statusId === 5) return 'success' // Approved, Conditionally Approved
    if (statusId === 6) return 'error' // Declined
    if (statusId === 3) return 'warning' // Underwriting
    if (statusId === 2) return 'info' // InProcess
    if (statusId === 1) return 'primary' // Submitted
    return 'default'
}

const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
    }).format(amount)
}

interface TabPanelProps {
    children?: React.ReactNode
    index: number
    value: number
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props

    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
        </div>
    )
}

export default function ApplicationDetailPage() {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const [application, setApplication] = useState<ApplicationDetail | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [updating, setUpdating] = useState(false)
    const [currentTab, setCurrentTab] = useState(0)
    const [decisionModalOpen, setDecisionModalOpen] = useState(false)
    const [pendingStatusId, setPendingStatusId] = useState<number | null>(null)
    const [decisionDetails, setDecisionDetails] = useState({
        approvedAmount: '',
        interestRate: '',
        termLength: '',
        paymentFrequency: 'Monthly',
        comments: '',
    })
    const [statusHistory, setStatusHistory] = useState<any[]>([])
    const [documents, setDocuments] = useState<any[]>([])

    const fetchApplication = async () => {
        if (!id) return

        try {
            setLoading(true)
            const data = await apiClient.getApplicationById(id)
            setApplication(data)
        } catch (err) {
            setError('Failed to load application')
            console.error(err)
        } finally {
            setLoading(false)
        }
    }

    const fetchStatusHistory = async () => {
        if (!id) return

        try {
            const history = await apiClient.getApplicationStatusHistory(id)
            setStatusHistory(history)
        } catch (err) {
            console.error('Failed to load status history:', err)
        }
    }

    const fetchDocuments = async () => {
        if (!id) return
        try {
            const docs = await apiClient.getApplicationDocuments(id)
            setDocuments(docs)
        } catch (err) {
            console.error('Failed to load documents:', err)
        }
    }

    useEffect(() => {
        fetchApplication()
        fetchStatusHistory()
        fetchDocuments()
    }, [id])

    const handleStatusClick = async (newStatusId: number) => {
        if (!id || !application || newStatusId === application.statusId || updating) return

        // Prevent moving back to Submitted status
        if (newStatusId === 1 && application.statusId !== 1) {
            setError('Cannot move application back to Submitted status')
            setTimeout(() => setError(null), 3000)
            return
        }

        // If status is Approved, ConditionallyApproved, or Declined, show decision details modal
        if ([4, 5, 6].includes(newStatusId)) {
            setPendingStatusId(newStatusId)
            setDecisionModalOpen(true)
            return
        }

        // For other statuses, update directly without decision details
        await updateApplicationStatus(newStatusId, null)
    }

    const handleDecisionModalClose = () => {
        setDecisionModalOpen(false)
        setPendingStatusId(null)
        setDecisionDetails({
            approvedAmount: '',
            interestRate: '',
            termLength: '',
            paymentFrequency: 'Monthly',
            comments: '',
        })
    }

    const handleDecisionModalSubmit = async () => {
        if (pendingStatusId === null) return

        const details = {
            approvedAmount: decisionDetails.approvedAmount ? parseFloat(decisionDetails.approvedAmount) : undefined,
            interestRate: decisionDetails.interestRate ? parseFloat(decisionDetails.interestRate) : undefined,
            termLength: decisionDetails.termLength ? parseInt(decisionDetails.termLength) : undefined,
            paymentFrequency: decisionDetails.paymentFrequency || undefined,
            comments: decisionDetails.comments || undefined,
        }

        // Remove undefined values
        const cleanedDetails = Object.fromEntries(
            Object.entries(details).filter(([_, v]) => v !== undefined)
        )

        await updateApplicationStatus(pendingStatusId, Object.keys(cleanedDetails).length > 0 ? cleanedDetails : null)
        handleDecisionModalClose()
    }

    const updateApplicationStatus = async (newStatusId: number, decisionDetails: any) => {
        if (!id) return

        try {
            setUpdating(true)
            await apiClient.updateApplicationStatus(id, newStatusId, decisionDetails)
            await fetchApplication()
            await fetchStatusHistory()
            setUpdating(false)
        } catch (err) {
            setError('Failed to update application status')
            console.error(err)
            setUpdating(false)
        }
    }

    if (loading) {
        return (
            <Container maxWidth="lg">
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                </Box>
            </Container>
        )
    }

    if (error || !application) {
        return (
            <Container maxWidth="lg">
                <Alert severity="error">{error || 'Application not found'}</Alert>
            </Container>
        )
    }

    return (
        <Container maxWidth="lg">
            <Box sx={{ mb: 3, display: 'flex', gap: 2, justifyContent: 'space-between' }}>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/applications')}>
                    Back to Applications
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SendIcon />}
                    onClick={() => navigate(`/applications/${id}/submittolenders`)}
                >
                    Submit to Lenders
                </Button>
            </Box>

            <Paper sx={{ mb: 3 }}>
                <Box sx={{ p: 3, pb: 2 }}>
                    <Typography variant="h4" component="h1" gutterBottom>
                        Application Details
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 2 }}>
                        <Chip label={application.statusName} color={getStatusColor(application.statusId)} size="medium" />
                        <Typography variant="body2" color="text.secondary">
                            ID: {application.id}
                        </Typography>
                    </Box>
                </Box>

                {/* Broker Information */}
                {application.broker && (
                    <Box sx={{ px: 3, pb: 2 }}>
                        <Card variant="outlined" sx={{ backgroundColor: '#f5f5f5' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip label="Broker" color="primary" size="small" />
                                    {application.broker.name}
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="body2" color="text.secondary">
                                            Contact
                                        </Typography>
                                        <Typography variant="body1">
                                            {application.broker.contactFirstName} {application.broker.contactLastName}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="body2" color="text.secondary">
                                            Email
                                        </Typography>
                                        <Typography variant="body1">{application.broker.contactEmail || 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="body2" color="text.secondary">
                                            Phone
                                        </Typography>
                                        <Typography variant="body1">{application.broker.contactPhone || 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="body2" color="text.secondary">
                                            Address
                                        </Typography>
                                        <Typography variant="body1">
                                            {[
                                                application.broker.address1,
                                                application.broker.address2,
                                                application.broker.city,
                                                application.broker.state,
                                                application.broker.postalCode,
                                            ]
                                                .filter(Boolean)
                                                .join(', ') || 'N/A'}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Box>
                )}

                {/* Status Indicator */}
                <Box sx={{ px: 3, pb: 2 }}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Application Status
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                                {statusOptions.map(status => {
                                    const isSubmittedStatus = status.id === 1
                                    const isCurrentStatus = status.id === application.statusId
                                    const cannotMoveToSubmitted = isSubmittedStatus && application.statusId !== 1
                                    const isDisabled = updating || cannotMoveToSubmitted

                                    return (
                                        <Chip
                                            key={status.id}
                                            label={status.label}
                                            color={isCurrentStatus ? getStatusColor(status.id) : 'default'}
                                            variant={isCurrentStatus ? 'filled' : 'outlined'}
                                            onClick={() => !isDisabled && handleStatusClick(status.id)}
                                            sx={{
                                                cursor: isDisabled ? 'not-allowed' : 'pointer',
                                                opacity: cannotMoveToSubmitted ? 0.4 : updating ? 0.6 : 1,
                                                fontWeight: isCurrentStatus ? 'bold' : 'normal',
                                                textDecoration: cannotMoveToSubmitted ? 'line-through' : 'none',
                                                '&:hover': {
                                                    transform: isDisabled ? 'none' : 'scale(1.05)',
                                                    transition: 'transform 0.2s',
                                                },
                                            }}
                                            disabled={isDisabled}
                                        />
                                    )
                                })}
                            </Box>
                            {application.statusId > 1 && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                    Note: Applications cannot be moved back to Submitted status
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Box>

                <Tabs value={currentTab} onChange={(_, newValue) => setCurrentTab(newValue)} sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
                    <Tab label="Overview" />
                    <Tab label="Business Details" />
                    <Tab label="Borrowers" />
                    <Tab label="Bank Information" />
                    <Tab label="Financing Terms" />
                    <Tab label="Timeline" />
                    <Tab label="Documents" />
                </Tabs>

                <Box sx={{ p: 3 }}>
                    {/* Tab 1: Overview */}
                    <TabPanel value={currentTab} index={0}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Description
                                </Typography>
                                <Typography variant="body1">{application.description || 'N/A'}</Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Source Application ID
                                </Typography>
                                <Typography variant="body1">{application.sourceApplicationId || 'N/A'}</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="h6" gutterBottom>
                                    Quick Summary
                                </Typography>
                            </Grid>
                            {application.business && (
                                <>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                            Business Name
                                        </Typography>
                                        <Typography variant="body1">
                                            {application.business.LegalName || application.business.DBAName || 'N/A'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                            TIN
                                        </Typography>
                                        <Typography variant="body1">{application.business.TIN || 'N/A'}</Typography>
                                    </Grid>
                                </>
                            )}
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Number of Borrowers
                                </Typography>
                                <Typography variant="body1">{application.borrowers?.length || 0}</Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Number of Financing Requests
                                </Typography>
                                <Typography variant="body1">{application.financingTermsRequests?.length || 0}</Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Number of Documents
                                </Typography>
                                <Typography variant="body1">{documents.length}</Typography>
                            </Grid>
                            {application.financingTermsRequests && application.financingTermsRequests.length > 0 && (
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Total Financing Amount Requested
                                    </Typography>
                                    <Typography variant="body1">
                                        {formatCurrency(
                                            application.financingTermsRequests.reduce((sum: number, term: any) => sum + term.Amount, 0)
                                        )}
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    </TabPanel>

                    {/* Tab 2: Business Details */}
                    <TabPanel value={currentTab} index={1}>
                        {application.business ? (
                            <>
                                <Typography variant="h6" gutterBottom>
                                    Business Information
                                </Typography>
                                <TableContainer>
                                    <Table>
                                        <TableBody>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Legal Name</TableCell>
                                                <TableCell>{application.business.LegalName || 'N/A'}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold' }}>DBA Name</TableCell>
                                                <TableCell>{application.business.DBAName || 'N/A'}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Legal Entity</TableCell>
                                                <TableCell>{application.business.LegalEntity || 'N/A'}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Parent Company Name</TableCell>
                                                <TableCell>{application.business.ParentCompanyName || 'N/A'}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold' }}>TIN</TableCell>
                                                <TableCell>{application.business.TIN || 'N/A'}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Business Description</TableCell>
                                                <TableCell>{application.business.BusinessDescription || 'N/A'}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Date Opened</TableCell>
                                                <TableCell>
                                                    {application.business.DateOpened
                                                        ? new Date(application.business.DateOpened).toLocaleDateString()
                                                        : 'N/A'}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold' }}>NAICS Code</TableCell>
                                                <TableCell>{application.business.NAICSCode || 'N/A'}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Rent or Own</TableCell>
                                                <TableCell>{application.business.RentOrOwn || 'N/A'}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Home Based Business</TableCell>
                                                <TableCell>
                                                    {application.business.HomeBasedBusiness === null
                                                        ? 'N/A'
                                                        : application.business.HomeBasedBusiness
                                                        ? 'Yes'
                                                        : 'No'}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Sole Proprietorship</TableCell>
                                                <TableCell>
                                                    {application.business.SoleProprietorship === null
                                                        ? 'N/A'
                                                        : application.business.SoleProprietorship
                                                        ? 'Yes'
                                                        : 'No'}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Online Presence</TableCell>
                                                <TableCell>{application.business.OnlinePresence || 'N/A'}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Number of Employees</TableCell>
                                                <TableCell>{application.business.NumberOfEmployees || 'N/A'}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Integration Object ID</TableCell>
                                                <TableCell>{application.business.IntegrationObjectId || 'N/A'}</TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                {/* Business Addresses */}
                                {application.business.addresses && application.business.addresses.length > 0 && (
                                    <>
                                        <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
                                            Addresses
                                        </Typography>
                                        <TableContainer>
                                            <Table>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Type</TableCell>
                                                        <TableCell>Street 1</TableCell>
                                                        <TableCell>Street 2</TableCell>
                                                        <TableCell>City</TableCell>
                                                        <TableCell>State</TableCell>
                                                        <TableCell>County</TableCell>
                                                        <TableCell>Postal Code</TableCell>
                                                        <TableCell>Country</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {application.business.addresses.map((address: any, idx: number) => (
                                                        <TableRow key={idx}>
                                                            <TableCell>{address.AddressTypeName || 'N/A'}</TableCell>
                                                            <TableCell>{address.Street1 || 'N/A'}</TableCell>
                                                            <TableCell>{address.Street2 || '-'}</TableCell>
                                                            <TableCell>{address.City || 'N/A'}</TableCell>
                                                            <TableCell>{address.State || 'N/A'}</TableCell>
                                                            <TableCell>{address.County || '-'}</TableCell>
                                                            <TableCell>{address.PostalCode || 'N/A'}</TableCell>
                                                            <TableCell>{address.Country || 'N/A'}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </>
                                )}

                                {/* Business Phones */}
                                {application.business.phones && application.business.phones.length > 0 && (
                                    <>
                                        <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
                                            Phone Numbers
                                        </Typography>
                                        <TableContainer>
                                            <Table>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Type</TableCell>
                                                        <TableCell>Country Code</TableCell>
                                                        <TableCell>Number</TableCell>
                                                        <TableCell>Extension</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {application.business.phones.map((phone: any, idx: number) => (
                                                        <TableRow key={idx}>
                                                            <TableCell>{phone.PhoneTypeName || 'N/A'}</TableCell>
                                                            <TableCell>{phone.CountryCode || 'N/A'}</TableCell>
                                                            <TableCell>{phone.Number || 'N/A'}</TableCell>
                                                            <TableCell>{phone.Extension || '-'}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </>
                                )}

                                {/* Business Lords */}
                                {application.business.businessLords && application.business.businessLords.length > 0 && (
                                    <>
                                        <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
                                            Business Lords (Landlords/Parent Companies)
                                        </Typography>
                                        {application.business.businessLords.map((lord: any, idx: number) => (
                                            <Card key={idx} variant="outlined" sx={{ mb: 2 }}>
                                                <CardContent>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                        {lord.TypeName}: {lord.Name || 'N/A'}
                                                    </Typography>
                                                    <Table size="small">
                                                        <TableBody>
                                                            <TableRow>
                                                                <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Contact Name</TableCell>
                                                                <TableCell>{lord.ContactName || 'N/A'}</TableCell>
                                                            </TableRow>
                                                            <TableRow>
                                                                <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                                                                <TableCell>{lord.Email || 'N/A'}</TableCell>
                                                            </TableRow>
                                                            <TableRow>
                                                                <TableCell sx={{ fontWeight: 'bold' }}>Phone</TableCell>
                                                                <TableCell>{lord.Phone || 'N/A'}</TableCell>
                                                            </TableRow>
                                                        </TableBody>
                                                    </Table>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </>
                                )}
                            </>
                        ) : (
                            <Alert severity="info">No business information available</Alert>
                        )}
                    </TabPanel>

                    {/* Tab 3: Borrowers */}
                    <TabPanel value={currentTab} index={2}>
                        {application.borrowers && application.borrowers.length > 0 ? (
                            <>
                                <Typography variant="h6" gutterBottom>
                                    Borrowers ({application.borrowers.length})
                                </Typography>
                                {application.borrowers.map((borrower: any, idx: number) => (
                                    <Card key={idx} variant="outlined" sx={{ mb: 3 }}>
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>
                                                Borrower #{idx + 1}
                                            </Typography>
                                            <TableContainer>
                                                <Table>
                                                    <TableBody>
                                                        <TableRow>
                                                            <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Full Name</TableCell>
                                                            <TableCell>
                                                                {[borrower.Prefix, borrower.FirstName, borrower.MiddleName, borrower.LastName, borrower.Suffix]
                                                                    .filter(Boolean)
                                                                    .join(' ') || 'N/A'}
                                                            </TableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <TableCell sx={{ fontWeight: 'bold' }}>Date of Birth</TableCell>
                                                            <TableCell>
                                                                {borrower.DateOfBirth ? new Date(borrower.DateOfBirth).toLocaleDateString() : 'N/A'}
                                                            </TableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <TableCell sx={{ fontWeight: 'bold' }}>SSN</TableCell>
                                                            <TableCell>{borrower.SSN ? `***-**-${borrower.SSN.slice(-4)}` : 'N/A'}</TableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <TableCell sx={{ fontWeight: 'bold' }}>Primary Email</TableCell>
                                                            <TableCell>{borrower.PrimaryEmail || 'N/A'}</TableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <TableCell sx={{ fontWeight: 'bold' }}>Ownership Percentage</TableCell>
                                                            <TableCell>{borrower.OwnershipPercentage ? `${borrower.OwnershipPercentage}%` : 'N/A'}</TableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <TableCell sx={{ fontWeight: 'bold' }}>Integration Object ID</TableCell>
                                                            <TableCell>{borrower.IntegrationObjectId || 'N/A'}</TableCell>
                                                        </TableRow>
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>

                                            {/* Borrower Addresses */}
                                            {borrower.addresses && borrower.addresses.length > 0 && (
                                                <>
                                                    <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
                                                        Addresses
                                                    </Typography>
                                                    <TableContainer>
                                                        <Table size="small">
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell>Type</TableCell>
                                                                    <TableCell>Street 1</TableCell>
                                                                    <TableCell>Street 2</TableCell>
                                                                    <TableCell>City</TableCell>
                                                                    <TableCell>State</TableCell>
                                                                    <TableCell>Postal Code</TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {borrower.addresses.map((address: any, addrIdx: number) => (
                                                                    <TableRow key={addrIdx}>
                                                                        <TableCell>{address.AddressTypeName || 'N/A'}</TableCell>
                                                                        <TableCell>{address.Street1 || 'N/A'}</TableCell>
                                                                        <TableCell>{address.Street2 || '-'}</TableCell>
                                                                        <TableCell>{address.City || 'N/A'}</TableCell>
                                                                        <TableCell>{address.State || 'N/A'}</TableCell>
                                                                        <TableCell>{address.PostalCode || 'N/A'}</TableCell>
                                                                    </TableRow>
                                                                ))}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                </>
                                            )}

                                            {/* Borrower Phones */}
                                            {borrower.phones && borrower.phones.length > 0 && (
                                                <>
                                                    <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
                                                        Phone Numbers
                                                    </Typography>
                                                    <TableContainer>
                                                        <Table size="small">
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell>Type</TableCell>
                                                                    <TableCell>Country Code</TableCell>
                                                                    <TableCell>Number</TableCell>
                                                                    <TableCell>Extension</TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {borrower.phones.map((phone: any, phoneIdx: number) => (
                                                                    <TableRow key={phoneIdx}>
                                                                        <TableCell>{phone.PhoneTypeName || 'N/A'}</TableCell>
                                                                        <TableCell>{phone.CountryCode || 'N/A'}</TableCell>
                                                                        <TableCell>{phone.Number || 'N/A'}</TableCell>
                                                                        <TableCell>{phone.Extension || '-'}</TableCell>
                                                                    </TableRow>
                                                                ))}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                </>
                                            )}
                                        </CardContent>
                                    </Card>
                                ))}
                            </>
                        ) : (
                            <Alert severity="info">No borrower information available</Alert>
                        )}
                    </TabPanel>

                    {/* Tab 4: Bank Information */}
                    <TabPanel value={currentTab} index={3}>
                        {application.bankInfos && application.bankInfos.length > 0 ? (
                            <>
                                <Typography variant="h6" gutterBottom>
                                    Bank Information ({application.bankInfos.length})
                                </Typography>
                                {application.bankInfos.map((bankInfo: any, idx: number) => (
                                    <Card key={idx} variant="outlined" sx={{ mb: 3 }}>
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>
                                                {bankInfo.BankName}
                                            </Typography>
                                            <Table>
                                                <TableBody>
                                                    <TableRow>
                                                        <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Bank Name</TableCell>
                                                        <TableCell>{bankInfo.BankName}</TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableCell sx={{ fontWeight: 'bold' }}>Friendly Name</TableCell>
                                                        <TableCell>{bankInfo.BankFriendlyName || 'N/A'}</TableCell>
                                                    </TableRow>
                                                </TableBody>
                                            </Table>

                                            {/* Bank Accounts */}
                                            {bankInfo.accounts && bankInfo.accounts.length > 0 && (
                                                <>
                                                    <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
                                                        Accounts ({bankInfo.accounts.length})
                                                    </Typography>
                                                    <TableContainer>
                                                        <Table size="small">
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell>Account Type</TableCell>
                                                                    <TableCell>Account Name</TableCell>
                                                                    <TableCell>Account Number</TableCell>
                                                                    <TableCell>Routing Number</TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {bankInfo.accounts.map((account: any, acctIdx: number) => (
                                                                    <TableRow key={acctIdx}>
                                                                        <TableCell>{account.AccountTypeName || 'N/A'}</TableCell>
                                                                        <TableCell>{account.AccountName || 'N/A'}</TableCell>
                                                                        <TableCell>****{account.AccountNumber?.slice(-4) || 'N/A'}</TableCell>
                                                                        <TableCell>{account.RoutingNumber || 'N/A'}</TableCell>
                                                                    </TableRow>
                                                                ))}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                </>
                                            )}
                                        </CardContent>
                                    </Card>
                                ))}
                            </>
                        ) : (
                            <Alert severity="info">No bank information available</Alert>
                        )}
                    </TabPanel>

                    {/* Tab 5: Financing Terms */}
                    <TabPanel value={currentTab} index={4}>
                        {application.financingTermsRequests && application.financingTermsRequests.length > 0 ? (
                            <>
                                <Typography variant="h6" gutterBottom>
                                    Financing Terms Requested ({application.financingTermsRequests.length})
                                </Typography>
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Priority</TableCell>
                                                <TableCell>Financing Type</TableCell>
                                                <TableCell>Amount</TableCell>
                                                <TableCell>Term Length</TableCell>
                                                <TableCell>Interest Rate</TableCell>
                                                <TableCell>Payment Frequency</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {application.financingTermsRequests.map((term: any, idx: number) => (
                                                <TableRow key={idx}>
                                                    <TableCell>{term.Priority}</TableCell>
                                                    <TableCell>{term.FinancingTypeName}</TableCell>
                                                    <TableCell>{formatCurrency(term.Amount)}</TableCell>
                                                    <TableCell>
                                                        {term.TermLength} {term.PaymentTermTypeName}
                                                    </TableCell>
                                                    <TableCell>{term.InterestRate}%</TableCell>
                                                    <TableCell>
                                                        Every {term.PaymentFrequencyLength} {term.PaymentFrequencyTypeName}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                {/* Detailed view for each term */}
                                <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
                                    Detailed Information
                                </Typography>
                                {application.financingTermsRequests.map((term: any, idx: number) => (
                                    <Card key={idx} variant="outlined" sx={{ mb: 2 }}>
                                        <CardContent>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                {term.FinancingTypeName} (Priority {term.Priority})
                                            </Typography>
                                            <Table size="small">
                                                <TableBody>
                                                    <TableRow>
                                                        <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>Financing Type</TableCell>
                                                        <TableCell>{term.FinancingTypeName}</TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableCell sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                                                        <TableCell>{formatCurrency(term.Amount)}</TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableCell sx={{ fontWeight: 'bold' }}>Payment Term Type</TableCell>
                                                        <TableCell>{term.PaymentTermTypeName}</TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableCell sx={{ fontWeight: 'bold' }}>Term Length</TableCell>
                                                        <TableCell>
                                                            {term.TermLength} {term.PaymentTermTypeName}
                                                        </TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableCell sx={{ fontWeight: 'bold' }}>Interest Rate</TableCell>
                                                        <TableCell>{term.InterestRate}%</TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableCell sx={{ fontWeight: 'bold' }}>Payment Frequency Type</TableCell>
                                                        <TableCell>{term.PaymentFrequencyTypeName}</TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableCell sx={{ fontWeight: 'bold' }}>Payment Frequency Length</TableCell>
                                                        <TableCell>Every {term.PaymentFrequencyLength}</TableCell>
                                                    </TableRow>
                                                </TableBody>
                                            </Table>
                                        </CardContent>
                                    </Card>
                                ))}
                            </>
                        ) : (
                            <Alert severity="info">No financing terms requested</Alert>
                        )}
                    </TabPanel>

                    {/* Tab 6: Timeline */}
                    <TabPanel value={currentTab} index={5}>
                        <Typography variant="h6" gutterBottom>
                            Application Timeline
                        </Typography>

                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 3, mb: 2 }}>
                            Important Dates
                        </Typography>
                        <TableContainer component={Paper} variant="outlined">
                            <Table>
                                <TableBody>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>Created Date</TableCell>
                                        <TableCell>{formatDate(application.createdDate)}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Submitted Date</TableCell>
                                        <TableCell>{formatDate(application.submittedDate)}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Approved Date</TableCell>
                                        <TableCell>{formatDate(application.approvedDate)}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Rejected Date</TableCell>
                                        <TableCell>{formatDate(application.rejectedDate)}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Cancelled Date</TableCell>
                                        <TableCell>{formatDate(application.cancelledDate)}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Closed Date</TableCell>
                                        <TableCell>{formatDate(application.closedDate)}</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Status Change History */}
                        {statusHistory.length > 0 && (
                            <>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 4, mb: 2 }}>
                                    Status Change History
                                </Typography>
                                {statusHistory.map((historyItem) => {
                                    let decisionDetailsObj = null
                                    if (historyItem.DecisionDetailsJson) {
                                        try {
                                            decisionDetailsObj = JSON.parse(historyItem.DecisionDetailsJson)
                                        } catch (e) {
                                            console.error('Failed to parse decision details:', e)
                                        }
                                    }

                                    return (
                                        <Card key={historyItem.Id} variant="outlined" sx={{ mb: 2 }}>
                                            <CardContent>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                        <Chip
                                                            label={historyItem.PreviousStatusName}
                                                            color={getStatusColor(historyItem.PreviousStatusId)}
                                                            size="small"
                                                        />
                                                        <Typography variant="body2">→</Typography>
                                                        <Chip
                                                            label={historyItem.NewStatusName}
                                                            color={getStatusColor(historyItem.NewStatusId)}
                                                            size="small"
                                                        />
                                                    </Box>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {formatDate(historyItem.ChangedDateTime)}
                                                    </Typography>
                                                </Box>

                                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                                    Changed by: {historyItem.ChangedByUserName} ({historyItem.ChangedByUserEmail})
                                                </Typography>

                                                {historyItem.Notes && (
                                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                                        <strong>Notes:</strong> {historyItem.Notes}
                                                    </Typography>
                                                )}

                                                {decisionDetailsObj && (
                                                    <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                                                        <Typography variant="subtitle2" gutterBottom>
                                                            Decision Details:
                                                        </Typography>
                                                        <Grid container spacing={2}>
                                                            {decisionDetailsObj.approvedAmount && (
                                                                <Grid item xs={12} sm={6}>
                                                                    <Typography variant="body2">
                                                                        <strong>Approved Amount:</strong> {formatCurrency(decisionDetailsObj.approvedAmount)}
                                                                    </Typography>
                                                                </Grid>
                                                            )}
                                                            {decisionDetailsObj.interestRate && (
                                                                <Grid item xs={12} sm={6}>
                                                                    <Typography variant="body2">
                                                                        <strong>Interest Rate:</strong> {decisionDetailsObj.interestRate}%
                                                                    </Typography>
                                                                </Grid>
                                                            )}
                                                            {decisionDetailsObj.termLength && (
                                                                <Grid item xs={12} sm={6}>
                                                                    <Typography variant="body2">
                                                                        <strong>Term Length:</strong> {decisionDetailsObj.termLength} months
                                                                    </Typography>
                                                                </Grid>
                                                            )}
                                                            {decisionDetailsObj.paymentFrequency && (
                                                                <Grid item xs={12} sm={6}>
                                                                    <Typography variant="body2">
                                                                        <strong>Payment Frequency:</strong> {decisionDetailsObj.paymentFrequency}
                                                                    </Typography>
                                                                </Grid>
                                                            )}
                                                            {decisionDetailsObj.comments && (
                                                                <Grid item xs={12}>
                                                                    <Typography variant="body2">
                                                                        <strong>Comments:</strong> {decisionDetailsObj.comments}
                                                                    </Typography>
                                                                </Grid>
                                                            )}
                                                        </Grid>
                                                    </Box>
                                                )}
                                            </CardContent>
                                        </Card>
                                    )
                                })}
                            </>
                        )}
                    </TabPanel>

                    {/* Tab 7: Documents */}
                    <TabPanel value={currentTab} index={6}>
                        <Typography variant="h6" gutterBottom>
                            Application Documents
                        </Typography>
                        {documents.length > 0 ? (
                            <TableContainer component={Paper} variant="outlined">
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Document Name</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Stipulation Type</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>File Type</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>File Size</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Upload Date</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {documents.map((doc: any) => (
                                            <TableRow key={doc.Id}>
                                                <TableCell>{doc.DocumentName}</TableCell>
                                                <TableCell>
                                                    {doc.StipulationType ? (
                                                        <Chip label={doc.StipulationType} size="small" color="primary" variant="outlined" />
                                                    ) : (
                                                        'N/A'
                                                    )}
                                                </TableCell>
                                                <TableCell>{doc.DocumentType || 'N/A'}</TableCell>
                                                <TableCell>
                                                    {doc.FileSize
                                                        ? doc.FileSize > 1048576
                                                            ? `${(doc.FileSize / 1048576).toFixed(1)} MB`
                                                            : `${(doc.FileSize / 1024).toFixed(1)} KB`
                                                        : 'N/A'}
                                                </TableCell>
                                                <TableCell>{formatDate(doc.CreatedDate)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Alert severity="info">No documents have been uploaded for this application</Alert>
                        )}
                    </TabPanel>
                </Box>
            </Paper>

            {/* Decision Details Modal */}
            <Dialog open={decisionModalOpen} onClose={handleDecisionModalClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {pendingStatusId === 4 && 'Approval Decision Details'}
                    {pendingStatusId === 5 && 'Conditional Approval Decision Details'}
                    {pendingStatusId === 6 && 'Decline Decision Details'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                        {(pendingStatusId === 4 || pendingStatusId === 5) && (
                            <>
                                <TextField
                                    label="Approved Amount"
                                    type="number"
                                    value={decisionDetails.approvedAmount}
                                    onChange={e => setDecisionDetails({ ...decisionDetails, approvedAmount: e.target.value })}
                                    fullWidth
                                    InputProps={{ startAdornment: '$' }}
                                />
                                <TextField
                                    label="Interest Rate (%)"
                                    type="number"
                                    value={decisionDetails.interestRate}
                                    onChange={e => setDecisionDetails({ ...decisionDetails, interestRate: e.target.value })}
                                    fullWidth
                                    inputProps={{ step: '0.01' }}
                                />
                                <TextField
                                    label="Term Length (months)"
                                    type="number"
                                    value={decisionDetails.termLength}
                                    onChange={e => setDecisionDetails({ ...decisionDetails, termLength: e.target.value })}
                                    fullWidth
                                />
                                <TextField
                                    label="Payment Frequency"
                                    select
                                    value={decisionDetails.paymentFrequency}
                                    onChange={e => setDecisionDetails({ ...decisionDetails, paymentFrequency: e.target.value })}
                                    fullWidth
                                >
                                    <MenuItem value="Weekly">Weekly</MenuItem>
                                    <MenuItem value="Bi-Weekly">Bi-Weekly</MenuItem>
                                    <MenuItem value="Monthly">Monthly</MenuItem>
                                    <MenuItem value="Quarterly">Quarterly</MenuItem>
                                    <MenuItem value="Annually">Annually</MenuItem>
                                </TextField>
                            </>
                        )}
                        <TextField
                            label="Comments"
                            multiline
                            rows={4}
                            value={decisionDetails.comments}
                            onChange={e => setDecisionDetails({ ...decisionDetails, comments: e.target.value })}
                            fullWidth
                            placeholder={
                                pendingStatusId === 6
                                    ? 'Reason for decline...'
                                    : pendingStatusId === 5
                                    ? 'Conditions for approval...'
                                    : 'Additional notes...'
                            }
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDecisionModalClose}>Cancel</Button>
                    <Button onClick={handleDecisionModalSubmit} variant="contained" color="primary" disabled={updating}>
                        {updating ? 'Updating...' : 'Update Status'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    )
}
